/**
 * Admin · Stores (§21, §6 / §B-1). MANAGER sees the read-only table; ADMIN gets
 * create / edit / delete plus the test-connection and re-ingest actions. Secret
 * columns are never fetched — only a present/missing status. Writes go through the
 * ADMIN API (audited, optimistic concurrency, secrets encrypted at rest).
 */
import { useCallback, useMemo, useState } from "react";
import { ApiError, api, type AdminStore } from "../../lib/api.js";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { formatAge } from "../../lib/age.js";
import { humanizeCode } from "../../lib/format.js";
import {
  Badge,
  StateChip,
  DataTable,
  type Column,
  Button,
  EmptyState,
  ErrorState,
  SkeletonRows,
  useToast,
} from "../../components/ui/index.js";
import { StoreEditor } from "./stores/StoreEditor.js";

export function Stores() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const r = useResource(api.admin.stores);
  const { toast } = useToast();
  const [editing, setEditing] = useState<AdminStore | null | "new">(null);
  const [busy, setBusy] = useState(false);

  const reload = r.reload;
  const stores = useMemo(() => r.data?.stores ?? [], [r.data]);

  const testConn = useCallback(
    async (s: AdminStore) => {
      setBusy(true);
      try {
        const res = await api.admin.testStoreConnection(s.id);
        toast(`${s.name}: ${res.ok ? "✓ " : "✗ "}${res.detail}`, res.ok ? "success" : "danger");
      } catch {
        toast("Couldn’t test the connection.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );

  const reingest = useCallback(
    async (s: AdminStore) => {
      if (!window.confirm(`Re-ingest orders for ${s.name}? This enqueues a background sweep.`)) return;
      setBusy(true);
      try {
        await api.admin.reingestStore(s.id, "sweep");
        toast(`${s.name}: re-ingest enqueued`, "success");
      } catch (e) {
        toast(e instanceof ApiError && e.status === 409 ? "Store is inactive — activate it first." : "Couldn’t enqueue re-ingest.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );

  const remove = useCallback(
    async (s: AdminStore) => {
      if (!window.confirm(`Delete store “${s.name}”? This cannot be undone.`)) return;
      setBusy(true);
      try {
        await api.admin.deleteStore(s.id);
        toast("Store deleted", "success");
        reload();
      } catch (e) {
        toast(e instanceof ApiError && e.status === 409 ? "Store has orders — deactivate it instead." : "Couldn’t delete the store.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [reload, toast],
  );

  const columns = useMemo<Array<Column<AdminStore>>>(() => {
    const base: Array<Column<AdminStore>> = [
      {
        key: "name",
        header: "Store",
        cell: (s) => (
          <span className="cell-stack">
            <span className="cell-strong">{s.name}</span>
            <span className="cell-sub">{s.shopDomain}</span>
          </span>
        ),
      },
      { key: "region", header: "Region", cell: (s) => <Badge>{s.region}</Badge> },
      { key: "currency", header: "Currency", cell: (s) => <span className="cell-mono">{s.currency}</span> },
      { key: "shortage", header: "Shortage policy", cell: (s) => (s.shortagePolicy ? humanizeCode(s.shortagePolicy) : <span className="muted">Default</span>) },
      {
        key: "creds",
        header: "Creds",
        cell: (s) => (
          <span className="q-carrier">
            <StateChip on={s.accessTokenStatus === "present"} onLabel="token" offLabel="no token" />
            <StateChip on={s.webhookSecretStatus === "present"} onLabel="hook" offLabel="no hook" />
          </span>
        ),
      },
      { key: "orders", header: "Orders", align: "right", cell: (s) => <span className="tnum">{s.orderCount}</span> },
      {
        key: "ingest",
        header: "Last ingest",
        cell: (s) => (s.lastReconcileAt ? <span title={s.lastReconcileAt}>{formatAge(s.lastReconcileAt)}</span> : <span className="muted">never</span>),
      },
      { key: "active", header: "Status", cell: (s) => <StateChip on={s.active} onLabel="Active" offLabel="Inactive" /> },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      {
        key: "actions",
        header: "",
        cell: (s) => (
          <span className="row-actions">
            <Button variant="ghost" size="sm" icon="bolt" iconOnly aria-label={`Test ${s.name} connection`} disabled={busy} onClick={() => void testConn(s)} />
            <Button variant="ghost" size="sm" icon="truck" iconOnly aria-label={`Re-ingest ${s.name}`} disabled={busy} onClick={() => void reingest(s)} />
            <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${s.name}`} onClick={() => setEditing(s)} />
            <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Delete ${s.name}`} disabled={busy} onClick={() => void remove(s)} />
          </span>
        ),
      },
    ];
  }, [isAdmin, busy, testConn, reingest, remove]);

  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Stores</h1>
          {r.data && <span className="page-head__count">{stores.length} stores</span>}
        </div>
        {isAdmin ? (
          <div className="page-head__actions">
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>
              Connect store
            </Button>
          </div>
        ) : (
          <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>
        )}
      </div>

      {r.error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
      ) : r.loading && !r.data ? (
        <div className="table-card"><SkeletonRows rows={5} cols={isAdmin ? 9 : 8} /></div>
      ) : stores.length === 0 ? (
        <EmptyState icon="store" title="No stores connected" hint={isAdmin ? "Connect your first Shopify store." : "An admin can connect a store."} />
      ) : (
        <div aria-busy={busy}>
          <DataTable ariaLabel="Stores" rows={stores} getRowKey={(x) => x.id} columns={columns} />
        </div>
      )}

      {editing !== null && (
        <StoreEditor
          store={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}
